import React, { useEffect } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Card } from 'primereact/card';
import { Tag } from 'primereact/tag';
import { Message } from 'primereact/message';
import { Toast } from 'primereact/toast';
import { Divider } from 'primereact/divider';
import { Skeleton } from 'primereact/skeleton';
import { ShippingOrder, ShippingLineItem } from '../DeShippingTableWrapper';
import { useOrderData } from '../hooks/useOrderData';
import { useLineItems } from '../hooks/useLineItems';
import { useAdminControlDialog } from '../hooks/useAdminControlDialog';
import { ConfirmAdminChangesDialog } from './ConfirmAdminChangesDialog';
import '../css/AdminControlDialog.css';
import { useOrderSelection } from '../hooks/useOrderSelection';
import { useLineItemSelection } from '../hooks/useLineItemSelection';

interface AdminControlDialogProps {
  orderData: ReturnType<typeof useOrderData>;
  orderSelection: ReturnType<typeof useOrderSelection>;
  lineItemsHook: ReturnType<typeof useLineItems>;
  lineItemSelection: ReturnType<typeof useLineItemSelection>;
  visible: boolean;
  onHide: () => void;
  order?: ShippingOrder;
}

export const AdminControlDialog: React.FC<AdminControlDialogProps> = ({
  orderData,
  orderSelection,
  lineItemsHook,
  lineItemSelection,
  visible,
  onHide,
}) => {
  const order = orderSelection.selectedOrder;
  const updateSingleOrder = orderData.refreshSingleOrder;

  const { 
    lineItems, 
    loading, 
    error, 
    refetch,
    updateLineItemsBulk,
    updating,
    updateError
  } = lineItemsHook;
  
  const {
    // State
    saving,
    saveError,
    showConfirmDialog,
    changesSummary,
    toastRef,
    hasChanges,
    
    // Options
    fulfillmentStatusOptions,
    fulfillmentLocationOptions,
    
    // Handlers
    handleSaveClick,
    handleConfirmedSave,
    handleConfirmDialogHide,
    updateLineItem,
    getLineItemValue,
    reset
  } = useAdminControlDialog({
    order: order || undefined,
    lineItems,
    updateLineItemsBulk,
    refetch,
    updateSingleOrder
  });


  const statusBodyTemplate = (lineItem: ShippingLineItem) => {
    const currentStatus = getLineItemValue(lineItem, 'fulfillment_status') as string;
    return (
      <Dropdown
        value={currentStatus}
        options={fulfillmentStatusOptions}
        onChange={(e) => updateLineItem(lineItem.line_item_id, 'fulfillment_status', e.value)}
        className="w-full"
        disabled={saving}
      />
    );
  };

  const locationBodyTemplate = (lineItem: ShippingLineItem) => {
    const currentLocation = getLineItemValue(lineItem, 'fulfillment_location') as string;
    return (
      <Dropdown
        value={currentLocation}
        options={fulfillmentLocationOptions}
        onChange={(e) => updateLineItem(lineItem.line_item_id, 'fulfillment_location', e.value)}
        placeholder="Select location"
        className="w-full"
        disabled={saving}
      />
    );
  };

  const declineReasonBodyTemplate = (lineItem: ShippingLineItem) => {
    const currentReason = getLineItemValue(lineItem, 'decline_reason') as string;
    const isDeclined = getLineItemValue(lineItem, 'fulfillment_status') === 'store_declined_order' || getLineItemValue(lineItem, 'fulfillment_status') === 'rejected_by_admin';
    
    return (
      <InputText
        value={currentReason || ''}
        onChange={(e) => updateLineItem(lineItem.line_item_id, 'decline_reason', e.target.value)}
        placeholder={isDeclined ? "Enter decline reason" : "N/A"}
        disabled={!isDeclined || saving}
        className="w-full"
      />
    );
  };

  const productBodyTemplate = (lineItem: ShippingLineItem) => {
    return (
      <div>
        <div className="font-semibold">{lineItem.product_name}</div>
        <div className="text-sm text-gray-600">SKU: {lineItem.product_sku}</div>
      </div>
    );
  };

  const quantityPriceBodyTemplate = (lineItem: ShippingLineItem) => {
    return (
      <div>
        <div>Qty: {lineItem.product_quantity}</div>
        <div>${lineItem.product_price.toFixed(2)}</div>
      </div>
    );
  };

  const renderLineItemsContent = () => {
    if (loading) {
      return (
        <DataTable 
          value={[{}]} // Single empty object for one skeleton row
          responsiveLayout="scroll"
          className="p-datatable-sm"
        >
          <Column 
            field="product" 
            header="Product" 
            body={() => (
              <div>
                <Skeleton width="200px" height="1.2rem" className="mb-2" />
                <Skeleton width="120px" height="0.9rem" className="mb-1" />
                <Skeleton width="150px" height="0.9rem" />
              </div>
            )}
            style={{ minWidth: '250px' }}
          />
          <Column 
            field="quantity_price" 
            header="Qty / Price" 
            body={() => (
              <div>
                <Skeleton width="60px" height="1rem" className="mb-1" />
                <Skeleton width="70px" height="1rem" />
              </div>
            )}
            style={{ minWidth: '120px' }}
          />
          <Column 
            field="fulfillment_status" 
            header="Status" 
            body={() => <Skeleton width="100%" height="2.5rem" />}
            style={{ minWidth: '150px' }}
          />
          <Column 
            field="fulfillment_location" 
            header="Location" 
            body={() => <Skeleton width="100%" height="2.5rem" />}
            style={{ minWidth: '180px' }}
          />
          <Column 
            field="decline_reason" 
            header="Decline Reason" 
            body={() => <Skeleton width="100%" height="2.5rem" />}
            style={{ minWidth: '200px' }}
          />
        </DataTable>
      );
    }

    if (error) {
      return (
        <div className="p-4">
          <Message 
            severity="error" 
            text={`Error loading line items: ${error}`}
            className="w-full"
          />
          <div className="mt-3">
            <Button 
              label="Retry" 
              icon="pi pi-refresh" 
              onClick={refetch}
              className="p-button-outlined"
            />
          </div>
        </div>
      );
    }

    if (lineItems.length === 0) {
      return (
        <div className="p-4 text-center">
          <Message 
            severity="info" 
            text="No line items found for this order."
            className="w-full"
          />
        </div>
      );
    }

    return (
      <DataTable 
        value={lineItems} 
        responsiveLayout="scroll"
        className="p-datatable-sm"
      >
        <Column 
          field="product" 
          header="Product" 
          body={productBodyTemplate}
          style={{ minWidth: '250px' }}
        />
        <Column 
          field="quantity_price" 
          header="Qty / Price" 
          body={quantityPriceBodyTemplate}
          style={{ minWidth: '120px' }}
        />
        <Column 
          field="fulfillment_status" 
          header="Status" 
          body={statusBodyTemplate}
          style={{ minWidth: '150px' }}
        />
        <Column 
          field="fulfillment_location" 
          header="Location" 
          body={locationBodyTemplate}
          style={{ minWidth: '180px' }}
        />
        <Column 
          field="decline_reason" 
          header="Decline Reason" 
          body={declineReasonBodyTemplate}
          style={{ minWidth: '200px' }}
        />
      </DataTable>
    );
  };



  const isSaveDisabled = !hasChanges || loading || saving;

  return (
    <>
      <Toast ref={toastRef} />
      <ConfirmAdminChangesDialog
        visible={showConfirmDialog}
        onHide={handleConfirmDialogHide}
        onConfirm={handleConfirmedSave}
        changesSummary={changesSummary}
        saving={saving}
      />
      <Dialog
        header={`Admin Control - Manage Order Fulfillment ${lineItems.length > 0 ? `(${lineItems.length} items)` : ''}`}
        visible={visible}
        onShow={reset}
        onHide={() => {
          onHide();
          reset();
        }}
        style={{ width: '90vw', maxWidth: '1200px' }}
        modal
        maximizable
        data-testid="admin-control-dialog"
        footer={
          <div className="flex justify-content-between">
            <div className="flex gap-2">
              <Button 
                label="Cancel"
                severity="danger"
                icon="pi pi-times" 
                onClick={onHide} 
                className="p-button-text"
                disabled={saving}
              />
              <Button 
                label="Save Changes"
                icon="pi pi-check"
                severity="success"
                onClick={handleSaveClick}
                disabled={isSaveDisabled}
                loading={saving}
              />
            </div>
          </div>
        }
      >
        <div className="flex flex-column gap-4">
          {/* Display save error if exists */}
          {saveError && (
            <Message 
              severity="error" 
              text={`Save Error: ${saveError}`}
              className="w-full mb-3"
            />
          )}

          {/* Display update error from hook if exists */}
          {updateError && (
            <Message 
              severity="error" 
              text={`Update Error: ${updateError}`}
              className="w-full mb-3"
            />
          )}

          {/* Order Summary Section - Read Only */}
          <Card title="Order Summary" className="mb-4">
            <div className="grid">
              <div className="col-12 md:col-6 lg:col-4 mb-3">
                <div className="field">
                  <span className="font-semibold">Order Name: </span>
                  <span className="text-lg">{order?.order_name}</span>
                </div>
              </div>
              <div className="col-12 md:col-6 lg:col-4 mb-3">
                <div className="field">
                  <span className="font-semibold">Customer Email: </span>
                  <span>{order?.email}</span>
                </div>
              </div>
              <div className="col-12 md:col-6 lg:col-4 mb-3">
                <div className="field">
                  <span className="font-semibold">Phone: </span>
                  <span>{order?.phone}</span>
                </div>
              </div>
              <div className="col-12 md:col-6 lg:col-4 mb-3">
                <div className="field">
                  <span className="font-semibold">Financial Status: </span>
                  <Tag 
                    value={order?.financial_status} 
                    severity={order?.financial_status === 'paid' ? 'success' : 'warning'}
                  />
                </div>
              </div>
              <div className="col-12 md:col-6 lg:col-4 mb-3">
                <div className="field">
                  <span className="font-semibold">Total Items: </span>
                  <span>{order?.total_items}</span>
                </div>
              </div>
              <div className="col-12 md:col-6 lg:col-4 mb-3">
                <div className="field">
                  <span className="font-semibold">Order Subtotal: </span>
                  <span>${order?.order_subtotal.toFixed(2)}</span>
                </div>
              </div>
              <div className="col-12 md:col-6 lg:col-4 mb-3">
                <div className="field">
                  <span className="font-semibold">Assigned Items: </span>
                  <span>{order?.assigned_items}</span>
                </div>
              </div>
              <div className="col-12 md:col-6 lg:col-4 mb-3">
                <div className="field">
                  <span className="font-semibold">Unassigned Items: </span>
                  <span>{order?.unassigned_items}</span>
                </div>
              </div>
            </div>
          </Card>

          {/* Line Items Management Section */}
          <Card title="Line Items Management">
            {renderLineItemsContent()}
          </Card>
        </div>
      </Dialog>
    </>
  );
};